// src/pages/TeamMemberDetail.js
import { ChevronLeft, Mail, Phone } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import axios from "../utils/axiosHandler";

const TeamMemberDetail = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [member, setMember] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchTeamMember = async () => {
      try {
        setLoading(true);
        const response = await axios.get(`/api/team-members/public/${slug}`);
        setMember(response.data);

        // Update page metadata
        document.title = `${response.data.name} - Team Member | BagBox`;

        // Add meta description
        const metaDesc = document.querySelector('meta[name="description"]') || document.createElement('meta');
        metaDesc.setAttribute('name', 'description');
        metaDesc.setAttribute('content', response.data.shortBio || `Learn more about ${response.data.name}, ${response.data.position} at BagBox.`);
        if (!document.querySelector('meta[name="description"]')) {
          document.head.appendChild(metaDesc);
        }

        setError(null);
      } catch (err) {
        console.error('Error fetching team member:', err);
        setError('This team member could not be found.');
        navigate('/team', { replace: true });
      } finally {
        setLoading(false);
      }
    };

    if (slug) {
      fetchTeamMember();
    }

    // Cleanup function
    return () => {
      document.title = 'BagBox';
      const metaDesc = document.querySelector('meta[name="description"]');
      if (metaDesc) {
        metaDesc.setAttribute('content', 'Custom packaging solutions for businesses across North America.');
      }
    };
  }, [slug, navigate]);

  // Render social media icons with links
  const renderSocialLinks = (socialLinks) => {
    if (!socialLinks || socialLinks.length === 0) return null;

    return (
      <div className="flex space-x-4 mb-6">
        {socialLinks.map((link, index) => {
          let icon;
          let bgColor = "bg-gray-100 hover:bg-gray-200";
          let textColor = "text-gray-700";

          switch (link.platform) {
            case 'linkedin':
              icon = (
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
                </svg>
              );
              bgColor = "bg-blue-100 hover:bg-blue-200";
              textColor = "text-blue-700";
              break;
            case 'twitter':
              icon = (
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M23.953 4.57a10 10 0 01-2.825.775 4.958 4.958 0 002.163-2.723c-.951.555-2.005.959-3.127 1.184a4.92 4.92 0 00-8.384 4.482C7.69 8.095 4.067 6.13 1.64 3.162a4.822 4.822 0 00-.666 2.475c0 1.71.87 3.213 2.188 4.096a4.904 4.904 0 01-2.228-.616v.06a4.923 4.923 0 003.946 4.827 4.996 4.996 0 01-2.212.085 4.936 4.936 0 004.604 3.417 9.867 9.867 0 01-6.102 2.105c-.39 0-.779-.023-1.17-.067a13.995 13.995 0 007.557 2.209c9.053 0 13.998-7.496 13.998-13.985 0-.21 0-.42-.015-.63A9.935 9.935 0 0024 4.59z" />
                </svg>
              );
              bgColor = "bg-blue-100 hover:bg-blue-200";
              textColor = "text-blue-500";
              break;
            case 'facebook':
              icon = (
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
                </svg>
              );
              bgColor = "bg-blue-100 hover:bg-blue-200";
              textColor = "text-blue-800";
              break;
            case 'instagram':
              icon = (
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 0C8.74 0 8.333.015 7.053.072 5.775.132 4.905.333 4.14.63c-.789.306-1.459.717-2.126 1.384S.935 3.35.63 4.14C.333 4.905.131 5.775.072 7.053.012 8.333 0 8.74 0 12s.015 3.667.072 4.947c.06 1.277.261 2.148.558 2.913.306.788.717 1.459 1.384 2.126.667.666 1.336 1.079 2.126 1.384.766.296 1.636.499 2.913.558C8.333 23.988 8.74 24 12 24s3.667-.015 4.947-.072c1.277-.06 2.148-.262 2.913-.558.788-.306 1.459-.718 2.126-1.384.666-.667 1.079-1.335 1.384-2.126.296-.765.499-1.636.558-2.913.06-1.28.072-1.687.072-4.947s-.015-3.667-.072-4.947c-.06-1.277-.262-2.149-.558-2.913-.306-.789-.718-1.459-1.384-2.126C21.319 1.347 20.651.935 19.86.63c-.765-.297-1.636-.499-2.913-.558C15.667.012 15.26 0 12 0zm0 2.16c3.203 0 3.585.016 4.85.071 1.17.055 1.805.249 2.227.415.562.217.96.477 1.382.896.419.42.679.819.896 1.381.164.422.36 1.057.413 2.227.057 1.266.07 1.646.07 4.85s-.015 3.585-.074 4.85c-.061 1.17-.256 1.805-.421 2.227-.224.562-.479.96-.899 1.382-.419.419-.824.679-1.38.896-.42.164-1.065.36-2.235.413-1.274.057-1.649.07-4.859.07-3.211 0-3.586-.015-4.859-.074-1.171-.061-1.816-.256-2.236-.421-.569-.224-.96-.479-1.379-.899-.421-.419-.69-.824-.9-1.38-.165-.42-.359-1.065-.42-2.235-.045-1.26-.061-1.649-.061-4.844 0-3.196.016-3.586.061-4.861.061-1.17.255-1.814.42-2.234.21-.57.479-.96.9-1.381.419-.419.81-.689 1.379-.898.42-.166 1.051-.361 2.221-.421 1.275-.045 1.65-.06 4.859-.06l.045.03zm0 3.678c-3.405 0-6.162 2.76-6.162 6.162 0 3.405 2.76 6.162 6.162 6.162 3.405 0 6.162-2.76 6.162-6.162 0-3.405-2.76-6.162-6.162-6.162zM12 16c-2.21 0-4-1.79-4-4s1.79-4 4-4 4 1.79 4 4-1.79 4-4 4zm7.846-10.405c0 .795-.646 1.44-1.44 1.44-.795 0-1.44-.646-1.44-1.44 0-.794.646-1.439 1.44-1.439.793-.001 1.44.645 1.44 1.439z" />
                </svg>
              );
              bgColor = "bg-pink-100 hover:bg-pink-200";
              textColor = "text-pink-600";
              break;
            case 'github':
              icon = (
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 .297c-6.63 0-12 5.373-12 12 0 5.303 3.438 9.8 8.205 11.385.6.113.82-.258.82-.577 0-.285-.01-1.04-.015-2.04-3.338.724-4.042-1.61-4.042-1.61C4.422 18.07 3.633 17.7 3.633 17.7c-1.087-.744.084-.729.084-.729 1.205.084 1.838 1.236 1.838 1.236 1.07 1.835 2.809 1.305 3.495.998.108-.776.417-1.305.76-1.605-2.665-.3-5.466-1.332-5.466-5.93 0-1.31.465-2.38 1.235-3.22-.135-.303-.54-1.523.105-3.176 0 0 1.005-.322 3.3 1.23.96-.267 1.98-.399 3-.405 1.02.006 2.04.138 3 .405 2.28-1.552 3.285-1.23 3.285-1.23.645 1.653.24 2.873.12 3.176.765.84 1.23 1.91 1.23 3.22 0 4.61-2.805 5.625-5.475 5.92.42.36.81 1.096.81 2.22 0 1.606-.015 2.896-.015 3.286 0 .315.21.69.825.57C20.565 22.092 24 17.592 24 12.297c0-6.627-5.373-12-12-12" />
                </svg>
              );
              bgColor = "bg-gray-100 hover:bg-gray-200";
              textColor = "text-gray-800";
              break;
            case 'website':
              icon = (
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M21.41 8.64v-.05a10 10 0 1 0-18.82 0v.05a9.86 9.86 0 0 0 0 6.72v.05a10 10 0 0 0 18.82 0v-.05a9.86 9.86 0 0 0 0-6.72zM4.26 14a7.82 7.82 0 0 1 0-4h1.86a16.73 16.73 0 0 0 0 4zm.82 2h1.4a12.15 12.15 0 0 0 1 2.57A8 8 0 0 1 5.08 16zm1.4-8h-1.4a8 8 0 0 1 2.37-2.57A12.15 12.15 0 0 0 6.48 8zM11 19.7A6.34 6.34 0 0 1 8.57 14H11zm0-7.7H8.14a14.36 14.36 0 0 1 0-4H11zm0-6H8.57A6.34 6.34 0 0 1 11 4.3zm7.92 0h-1.4a12.15 12.15 0 0 0-1-2.57A8 8 0 0 1 18.92 6zM13 4.3A6.34 6.34 0 0 1 15.43 10H13zm0 15.4V16h2.43A6.34 6.34 0 0 1 13 19.7zm2.86-5.7a14.36 14.36 0 0 1 0-4h2.86a14.36 14.36 0 0 1 0 4zm.29 5.27a12.15 12.15 0 0 0 1-2.57h1.4a8 8 0 0 1-2.4 2.57zM19.74 14h-1.86a16.73 16.73 0 0 0 0-4h1.86a7.82 7.82 0 0 1 0 4z" />
                </svg>
              );
              bgColor = "bg-green-100 hover:bg-green-200";
              textColor = "text-green-700";
              break;
            case 'email':
              icon = (
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 12.713l-11.985-9.713h23.97l-11.985 9.713zm0 2.574l-12-9.725v15.438h24v-15.438l-12 9.725z" />
                </svg>
              );
              bgColor = "bg-red-100 hover:bg-red-200";
              textColor = "text-red-700";
              break;
            default:
              icon = (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                </svg>
              );
          }

          return (
            <a
              key={index}
              href={link.url}
              target="_blank"
              rel="noopener noreferrer"
              className={`${bgColor} ${textColor} p-3 rounded-full flex items-center justify-center transition-colors`}
              aria-label={`${link.platform} profile`}
            >
              {icon}
            </a>
          );
        })}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-16 max-w-6xl">
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#033568]"></div>
        </div>
      </div>
    );
  }

  if (error || !member) {
    return (
      <div className="container mx-auto px-4 py-16 max-w-6xl">
        <div className="bg-white shadow-lg rounded-xl p-8 text-center">
          <h1 className="text-2xl font-bold text-gray-800 mb-4">Team member not found</h1>
          <p className="text-gray-600 mb-6">The team member you're looking for could not be found.</p>
          <Link to="/" className="inline-flex items-center text-[#033568] hover:text-[#01254A]">
            <ChevronLeft className="w-5 h-5 mr-1" />
            Back to Home
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gray-50 min-h-screen">
      <div className="container mx-auto px-4 py-16 max-w-6xl">
        {/* Back navigation */}
        <div className="mb-8">
          <Link to="/" className="inline-flex items-center text-[#033568] hover:text-[#01254A] font-medium">
            <ChevronLeft className="w-5 h-5 mr-1" />
            Back to Home
          </Link>
        </div>

        {/* Main content */}
        <div className="bg-white shadow-xl rounded-2xl overflow-hidden">
          {/* Header area */}
          <div className="bg-gradient-to-br from-[#033568] to-[#01254A] text-white p-12 md:flex items-center">
            <div className="mb-8 md:mb-0 md:mr-12">
              {member.photoUrl ? (
                <div className="w-48 h-48 rounded-full overflow-hidden border-4 border-white shadow-xl mx-auto md:mx-0">
                  <img
                    src={member.photoUrl}
                    alt={member.name}
                    className="w-full h-full object-cover"
                  />
                </div>
              ) : (
                <div className="w-48 h-48 rounded-full bg-white flex items-center justify-center border-4 border-white shadow-xl text-[#033568] mx-auto md:mx-0">
                  <svg className="w-24 h-24" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                </div>
              )}
            </div>

            <div className="text-center md:text-left flex-1">
              <h1 className="text-4xl font-bold mb-2">{member.name}</h1>
              <h2 className="text-xl text-amber-300 font-medium mb-4">{member.position}</h2>
              {member.department && (
                <p className="text-white/80 mb-4">Department: {member.department}</p>
              )}

              <div className="flex flex-wrap justify-center md:justify-start space-x-4 mb-4">
                {member.email && (
                  <a
                    href={`mailto:${member.email}`}
                    className="bg-white/20 hover:bg-white/30 text-white rounded-full px-4 py-2 inline-flex items-center mt-2"
                  >
                    <Mail className="w-4 h-4 mr-2" /> {member.email}
                  </a>
                )}

                {member.phone && (
                  <a
                    href={`tel:${member.phone}`}
                    className="bg-white/20 hover:bg-white/30 text-white rounded-full px-4 py-2 inline-flex items-center mt-2"
                  >
                    <Phone className="w-4 h-4 mr-2" /> {member.phone}
                  </a>
                )}
              </div>
            </div>
          </div>

          {/* Content area */}
          <div className="p-12">
            <div className="grid md:grid-cols-3 gap-12">
              {/* Main column */}
              <div className="md:col-span-2">
                <h2 className="text-2xl font-bold text-gray-900 mb-6">About {member.name.split(' ')[0]}</h2>

                {member.bio ? (
                  <div className="prose max-w-none mb-8 text-justify">
                    {member.bio.split('\n').map((paragraph, idx) => (
                      <p key={idx} className="mb-4 text-gray-700 leading-relaxed">
                        {paragraph}
                      </p>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-700 mb-8">
                    {member.shortBio || `${member.name} is a valued member of our team at BagBox.`}
                  </p>
                )}

                {member.achievements && member.achievements.length > 0 && (
                  <div className="mb-8">
                    <h3 className="text-xl font-semibold text-gray-900 mb-4">Achievements</h3>
                    <ul className="list-disc pl-6 space-y-2">
                      {member.achievements.map((achievement, index) => (
                        <li key={index} className="text-gray-700">{achievement}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>

              {/* Sidebar */}
              <div>
                {/* Social Links */}
                {member.socialLinks && member.socialLinks.length > 0 && (
                  <div className="mb-8">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Connect with {member.name.split(' ')[0]}</h3>
                    {renderSocialLinks(member.socialLinks)}
                  </div>
                )}

                {/* Skills */}
                {member.skills && member.skills.length > 0 && (
                  <div className="mb-8">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Skills</h3>
                    <div className="flex flex-wrap gap-2">
                      {member.skills.map((skill, index) => (
                        <span
                          key={index}
                          className="bg-blue-50 text-blue-700 px-3 py-1 rounded-full text-sm"
                        >
                          {skill}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Education */}
                {member.education && member.education.length > 0 && (
                  <div className="mb-8">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Education</h3>
                    <ul className="space-y-2">
                      {member.education.map((edu, index) => (
                        <li key={index} className="text-gray-700">{edu}</li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Credentials */}
                {member.credentials && member.credentials.length > 0 && (
                  <div className="mb-8">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Credentials</h3>
                    <ul className="space-y-2">
                      {member.credentials.map((credential, index) => (
                        <li key={index} className="text-gray-700">{credential}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TeamMemberDetail;